<script setup lang="ts">
    import { computed, inject, ref } from 'vue'
    import { storeToRefs } from 'pinia'
    import { useI18n } from 'vue-i18n'
    import type { Chat as Conversation, UIChatMessage } from 'models'
    import { stripMarkdown, getTextPart } from 'utils'
    import {
        useChatbotStore,
        useConversationActions,
        useDialogConfirm,
    } from 'composables'
    import PkRelativeTime from '../PkRelativeTime.vue'
    import { localizeSummaryLabels } from './utils'
    import { ChatbotProfileContextKey } from './chatbotProfileContext'

    const props = defineProps<{ agentId: string }>()

    const { t: $t } = useI18n({ useScope: 'global' })

    const store = useChatbotStore(props.agentId)
    const {
        conversations,
        isLoadingConversations,
        localChatId,
        runningChatIds,
    } = storeToRefs(store)
    const { navigate, startNewChat, renameChatTitle, deleteChat } = store

    const {
        editingId,
        editingTitle,
        startEdit: startEditConversation,
        confirmEdit,
        cancelEdit,
        onRenameKeydown,
        requestDelete: handleDelete,
    } = useConversationActions({ renameChatTitle, deleteChat })

    const openConversation = (id: string) => {
        if (editingId.value) {
            return
        }
        localChatId.value = id
        navigate('chat')
    }

    const isCurrentConversation = (conversation: Conversation) => {
        return localChatId.value === conversation.id
    }

    const getLastMessage = (
        messages: UIChatMessage[],
        role: 'user' | 'assistant',
    ) => {
        return stripMarkdown(
            getTextPart(messages.filter((m) => m.role === role).slice(-1)[0]) ||
                '',
        )
    }

    const startEdit = (conversation: Conversation) => {
        startEditConversation(
            conversation,
            getLastMessage(conversation.messages, 'assistant'),
        )
    }

    const { openDialogConfirm } = useDialogConfirm()

    // GDPR B.3 — bulk "delete my conversations", available to anonymous
    // sessions too (the host wires the endpoint through the profile context).
    // Hidden when the host application owns the session (external token or
    // host-session mode): the endpoint erases ALL conversations of the
    // session's user platform-wide, an account-scoped action that belongs to
    // the host, not to an embedded widget. Same gating as sign-out and
    // manage-profile in PkChatbotViewProfile.
    const profile = inject(ChatbotProfileContextKey, undefined)
    const canEraseConversations = computed(
        () =>
            !!profile?.eraseConversations &&
            !(profile.isExternalAuth?.value ?? false),
    )
    const isErasingConversations = ref(false)
    const handleEraseAll = async () => {
        const eraseConversations = profile?.eraseConversations
        if (!eraseConversations || !canEraseConversations.value) {
            return
        }
        const proceed = await openDialogConfirm({
            title: $t('confirmTitle.deleteMyConversations'),
            questionLabel: $t('message.deleteMyConversationsConfirm'),
        })
        if (!proceed) {
            return
        }
        isErasingConversations.value = true
        try {
            await eraseConversations()
            conversations.value = []
            startNewChat()
        } catch {
            // Same policy as single delete: fail silently and reconcile
            // with the server state
            store.loadConversations(props.agentId)
        } finally {
            isErasingConversations.value = false
        }
    }
</script>

<template>
    <div class="flex flex-col flex-1 min-h-0 relative">
        <!-- Loading skeleton -->
        <div
            v-if="isLoadingConversations && conversations.length === 0"
            class="vv-item-group vv-item-group--fill">
            <div class="vv-item-group__list">
                <div
                    v-for="i in 5"
                    :key="i"
                    class="vv-item vv-item--roomy vv-item--plain">
                    <div class="vv-item__entry">
                        <div class="flex flex-col gap-6 flex-1 min-w-0">
                            <div
                                class="vv-skeleton h-16 rounded"
                                :class="i % 2 ? 'w-192' : 'w-128'">
                                <div class="vv-skeleton__item" />
                            </div>
                            <div class="vv-skeleton h-12 rounded">
                                <div class="vv-skeleton__item" />
                            </div>
                            <div class="vv-skeleton h-12 rounded w-160">
                                <div class="vv-skeleton__item" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Empty state -->
        <div
            v-else-if="conversations.length === 0"
            class="flex flex-col flex-1 items-center justify-center gap-12 p-24 text-center">
            <div
                class="w-64 h-64 rounded-full bg-surface-1 border border-surface-3 flex items-center justify-center">
                <VvIcon name="ri:chat-3-line" class="w-28 h-28 text-word-4" />
            </div>
            <div class="flex flex-col gap-4">
                <strong class="text-16 font-semibold text-word-1">
                    {{ $t('message.noConversations') }}
                </strong>
                <span class="text-14 text-word-3 leading-relaxed">
                    {{ $t('message.noConversationsHint') }}
                </span>
            </div>
            <VvButton
                modifiers="primary rounded"
                icon="ri:add-line"
                class="mt-8"
                :label="$t('action.startNewChat')"
                @click="startNewChat()" />
        </div>

        <!-- Conversation list. The row is a container, not a button: the menu
             and the rename field are siblings of the entry, because a button
             cannot legally contain other controls (Firefox and Safari drop
             them from the accessibility tree). -->
        <div v-else class="vv-item-group vv-item-group--fill">
            <ul
                class="vv-item-group__list overflow-y-auto pb-64 light-scrollbar">
                <li
                    v-for="conversation in conversations"
                    :key="conversation.id"
                    class="vv-item vv-item--roomy"
                    :class="{
                        'vv-item--current': isCurrentConversation(conversation),
                    }">
                    <div
                        v-if="editingId === conversation.id"
                        class="flex flex-1 items-center gap-8 min-w-0 px-md py-12">
                        <VvInputText
                            v-model="editingTitle"
                            class="flex-1 mb-0"
                            modifiers="compact"
                            autofocus
                            select-on-focus
                            :maxlength="255"
                            :aria-label="$t('placeholder.insert')"
                            :placeholder="$t('placeholder.insert')"
                            @keydown="
                                onRenameKeydown($event, conversation.id)
                            " />
                        <VvButtonGroup modifiers="compact">
                            <VvButton
                                icon="ri:save-line"
                                :title="$t('action.save')"
                                modifiers="action-quiet-small"
                                @click="confirmEdit(conversation.id)" />
                            <VvButton
                                icon="ri:close-line"
                                :title="$t('action.cancel')"
                                modifiers="action-quiet-small"
                                @click="cancelEdit()" />
                        </VvButtonGroup>
                    </div>
                    <template v-else>
                        <button
                            type="button"
                            class="vv-item__entry"
                            :aria-current="
                                isCurrentConversation(conversation)
                                    ? 'true'
                                    : undefined
                            "
                            @click="openConversation(conversation.id)">
                            <div class="min-w-0 flex-1">
                                <div class="flex items-center gap-8">
                                    <strong
                                        class="vv-item__title font-medium flex-1">
                                        {{
                                            stripMarkdown(conversation.title) ||
                                            getLastMessage(
                                                conversation.messages,
                                                'assistant',
                                            )
                                        }}
                                    </strong>
                                    <VvIcon
                                        v-if="
                                            runningChatIds.includes(
                                                conversation.id,
                                            )
                                        "
                                        name="loading-loop"
                                        role="img"
                                        :title="
                                            $t('label.answeringInBackground')
                                        "
                                        :aria-label="
                                            $t('label.answeringInBackground')
                                        "
                                        class="w-16 h-16 shrink-0 text-word-3" />
                                    <PkRelativeTime
                                        class="text-word-4 text-smaller shrink-0"
                                        :date="conversation.lastMessageAt" />
                                </div>
                                <span class="text-12 text-word-4 line-clamp-2">
                                    {{
                                        stripMarkdown(
                                            localizeSummaryLabels(
                                                conversation.summary,
                                                $t,
                                            ),
                                        ) ||
                                        getLastMessage(
                                            conversation.messages,
                                            'user',
                                        )
                                    }}
                                </span>
                            </div>
                        </button>
                        <div class="vv-item__actions">
                            <VvDropdown
                                v-bind="{
                                    placement: 'bottom-end',
                                    modifiers: 'menu',
                                    flip: true,
                                    offset: 3,
                                    strategy: 'fixed',
                                }">
                                <VvButton
                                    icon="ri:more-2-fill"
                                    :title="$t('action.moreActions')"
                                    modifiers="action-quiet-small" />
                                <template #items>
                                    <VvDropdownAction
                                        @click="startEdit(conversation)">
                                        <VvIcon name="ri:pencil-line" />
                                        {{ $t('action.renameChat') }}
                                    </VvDropdownAction>
                                    <VvDropdownAction
                                        @click="handleDelete(conversation.id)">
                                        <VvIcon name="ri:delete-bin-line" />
                                        {{ $t('action.delete') }}
                                    </VvDropdownAction>
                                </template>
                            </VvDropdown>
                        </div>
                    </template>
                </li>
            </ul>
        </div>

        <!-- Sticky "new chat" CTA (hidden on empty state, which has its own CTA) -->
        <div
            v-if="conversations.length > 0"
            class="absolute bottom-0 left-0 right-0 flex flex-col items-center gap-4 px-16 pb-16 pt-32 pointer-events-none"
            :style="{
                background:
                    'linear-gradient(to top, var(--color-surface) 55%, transparent)',
            }">
            <VvButton
                class="text-14 pointer-events-auto"
                modifiers="primary rounded"
                icon="ri:add-line"
                :label="$t('action.startNewChat')"
                @click.stop="startNewChat()" />
            <VvButton
                v-if="canEraseConversations"
                class="text-12 pointer-events-auto"
                modifiers="action-quiet-danger"
                icon="ri:delete-bin-line"
                :loading="isErasingConversations"
                :disabled="isErasingConversations"
                :label="$t('action.deleteMyConversations')"
                @click.stop="handleEraseAll()" />
        </div>
    </div>
</template>
